import type {DraftGuessModelV1} from '@albert-io/models';

import type {DraftInfo, GetState, SetState} from './types';
import {getDraftGuess, patchDraftGuess, saveDraftGuess} from '../../QuestionType.queries';
import {saveStatus} from 'client/components/QuestionTypes/QuestionType.types';

const actions = (set: SetState, get: GetState) => ({
  hasDraftGuess: (questionId) => {
    const draftGuess = get().draftByQuestionId[questionId]?.draft || null;
    return draftGuess !== null;
  },
  buildTimeElapsed: (questionId: string) => {
    const draftInfo = get().draftByQuestionId[questionId];
    if (!draftInfo) return 0;

    const {editStartTime, draft} = draftInfo;
    const prevTimeElapsed = draft?.getTimeElapsed() || 0;

    if (!editStartTime) return prevTimeElapsed;

    const newTimeElapsed = Date.now() - editStartTime.getTime();
    return newTimeElapsed + prevTimeElapsed;
  }, // time
  loadDraftGuess: async (assignmentId: string | null, questionId: string) => {
    if (!assignmentId) return;
    if (!questionId) return;
    let draftInfo: DraftInfo;

    try {
      const draftGuessResponse = await getDraftGuess(questionId, assignmentId);
      draftInfo = {
        draft: draftGuessResponse.first(),
        saveStatus: saveStatus.saved,
        editStartTime: null
      };
    } catch (_err) {
      draftInfo = {
        draft: null,
        saveStatus: saveStatus.loadError,
        editStartTime: null
      };
    }

    set((state) => {
      state.draftByQuestionId[questionId] = draftInfo;
    });

    return draftInfo.draft;
  },
  saveDraftGuess: async (assignmentId: string | null, questionId: string, draftContent: any) => {
    if (!assignmentId) return;

    set((state) => {
      if (!state.draftByQuestionId[questionId]) {
        state.draftByQuestionId[questionId] = {
          draft: null,
          saveStatus: saveStatus.saving,
          editStartTime: null
        };
      } else {
        state.draftByQuestionId[questionId].saveStatus = saveStatus.saving;
      }
    });

    const payload = {
      timeElapsed: get().buildTimeElapsed(questionId),
      content: draftContent
    };

    try {
      // Resolve the draft to PATCH. A CREATE may already be in flight from a prior rapid save
      // (e.g. placing several chips in quick succession); await it and PATCH the resulting draft
      // rather than POSTing a second create, which the (user, assignment, question) unique index
      // would reject with a 400.
      let currentDraft = get().draftByQuestionId[questionId]?.draft || null;
      if (!currentDraft) {
        const inFlightCreate = get().createInFlightByQuestionId[questionId];
        if (inFlightCreate) currentDraft = await inFlightCreate;
      }

      let draftGuess: DraftGuessModelV1;
      if (currentDraft) {
        const patchPromise = patchDraftGuess(currentDraft.getId(), payload);
        set({draftGuessPromise: patchPromise});
        draftGuess = await patchPromise;
      } else {
        const createPromise = saveDraftGuess(payload, questionId, assignmentId);
        set((state) => {
          state.createInFlightByQuestionId[questionId] = createPromise;
        });
        set({draftGuessPromise: createPromise});
        try {
          draftGuess = await createPromise;
        } finally {
          set((state) => {
            delete state.createInFlightByQuestionId[questionId];
          });
        }
      }

      set((state) => {
        state.draftByQuestionId[questionId] = {
          ...state.draftByQuestionId[questionId],
          draft: draftGuess,
          saveStatus: saveStatus.saved
        };
      });
      // Restart the edit timer after saving so we can track additional time spent on this question.
      get().startTimer(questionId);
    } catch (_err) {
      set((state) => {
        if (state.draftByQuestionId[questionId]) {
          state.draftByQuestionId[questionId].saveStatus = saveStatus.saveError;
        }
      });
    } finally {
      set((state) => {
        state.draftGuessPromise = null;
      });
    }
  },
  shouldNotSaveDraft: () => true,
  startTimer: (questionId) => {
    set((state) => {
      if (!state.draftByQuestionId[questionId]) {
        state.draftByQuestionId[questionId] = {
          draft: null,
          saveStatus: saveStatus.saving,
          editStartTime: new Date()
        };
      } else {
        state.draftByQuestionId[questionId].editStartTime = new Date();
      }
    });
  }
});

export default actions;
